/**
 * Authorization React hooks — connects the ABAC policy engine
 * to React components via the Zustand app store.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppStore } from '@/store/appStore'
import * as authService from '@/services/authorizationService'
import type {
  Action,
  ResourceType,
  SystemRole,
  PortfolioRole,
  AuthorizationDecision,
  EffectivePermission,
} from '@/types/authorization'

// ---------------------------------------------------------------------------
// Initialization hook — call once at app root
// ---------------------------------------------------------------------------

export function useAuthInitialize() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    authService.initializeAuthorization()
      .then(() => setReady(true))
      .catch((err) => setError(String(err)))
  }, [])

  return { ready, error }
}

// ---------------------------------------------------------------------------
// Core permission hook
// ---------------------------------------------------------------------------

/**
 * Check if the current user has permission to perform an action on a resource.
 *
 * @example
 * const { allowed } = usePermission('edit', 'Project')
 * const { allowed } = usePermission('view', 'Budget', portfolioId)
 */
export function usePermission(
  action: Action,
  resourceType: ResourceType,
  resourcePortfolioId?: string,
): AuthorizationDecision {
  const currentUser = useAppStore((s) => s.currentUser)
  const selectedPortfolio = useAppStore((s) => s.selectedPortfolio)

  return useMemo(() => {
    const systemRole = resolveSystemRole(currentUser.role)
    const portfolioRole = resolvePortfolioRole(currentUser.role)
    const portfolioId = resourcePortfolioId ?? selectedPortfolio?.id

    return authService.checkPermission(
      currentUser.id,
      systemRole,
      portfolioRole,
      currentUser.assignedPortfolios,
      currentUser.clearance,
      currentUser.department,
      action,
      resourceType,
      portfolioId,
    )
  }, [currentUser, selectedPortfolio, action, resourceType, resourcePortfolioId])
}

// ---------------------------------------------------------------------------
// Nav item permission hook
// ---------------------------------------------------------------------------

/**
 * Check if a sidebar nav item should be visible for the current user.
 */
export function useNavPermission(navKey: string): boolean {
  const currentUser = useAppStore((s) => s.currentUser)

  return useMemo(() => {
    const systemRole = resolveSystemRole(currentUser.role)
    const portfolioRole = resolvePortfolioRole(currentUser.role)

    return authService.isNavItemAllowed(
      navKey,
      systemRole,
      portfolioRole,
      currentUser.assignedPortfolios,
      currentUser.clearance,
    )
  }, [currentUser, navKey])
}

/**
 * Filter an array of nav items by permission.
 */
export function useFilteredNavItems<T extends { key: string }>(items: T[]): T[] {
  const currentUser = useAppStore((s) => s.currentUser)

  return useMemo(() => {
    const systemRole = resolveSystemRole(currentUser.role)
    const portfolioRole = resolvePortfolioRole(currentUser.role)

    return items.filter(item =>
      authService.isNavItemAllowed(
        item.key,
        systemRole,
        portfolioRole,
        currentUser.assignedPortfolios,
        currentUser.clearance,
      )
    )
  }, [currentUser, items])
}

// ---------------------------------------------------------------------------
// UI component access hook
// ---------------------------------------------------------------------------

/**
 * Check if a UI component should be visible/enabled based on UI policy mappings.
 *
 * @example
 * const { visible, enabled } = useComponentAccess('editProjectButton')
 */
export function useComponentAccess(componentId: string): { visible: boolean; enabled: boolean } {
  const currentUser = useAppStore((s) => s.currentUser)

  return useMemo(() => {
    const systemRole = resolveSystemRole(currentUser.role)
    const portfolioRole = resolvePortfolioRole(currentUser.role)

    return authService.getUiComponentAccess(
      componentId,
      systemRole,
      portfolioRole,
      currentUser.assignedPortfolios,
      currentUser.clearance,
    )
  }, [currentUser, componentId])
}

// ---------------------------------------------------------------------------
// Effective permissions hook (for admin preview)
// ---------------------------------------------------------------------------

export function useEffectivePermissions(portfolioId?: string): EffectivePermission[] {
  const currentUser = useAppStore((s) => s.currentUser)

  return useMemo(() => {
    const systemRole = resolveSystemRole(currentUser.role)
    const portfolioRole = resolvePortfolioRole(currentUser.role)

    return authService.getEffectivePermissions(
      systemRole,
      portfolioRole,
      currentUser.assignedPortfolios,
      currentUser.clearance,
      currentUser.department,
      portfolioId,
    )
  }, [currentUser, portfolioId])
}

// ---------------------------------------------------------------------------
// Configuration context access
// ---------------------------------------------------------------------------

/**
 * Check if current user can access the configuration context.
 */
export function useCanAccessConfig(): boolean {
  const currentUser = useAppStore((s) => s.currentUser)

  return useMemo(() => {
    const systemRole = resolveSystemRole(currentUser.role)
    return systemRole === 'GlobalAdmin' || systemRole === 'ConfigAdmin'
  }, [currentUser])
}

// ---------------------------------------------------------------------------
// Audit hook
// ---------------------------------------------------------------------------

export function useAuthAuditLog() {
  const [log, setLog] = useState(authService.getAuthAuditLog())

  const refresh = useCallback(() => {
    setLog(authService.getAuthAuditLog())
  }, [])

  return { log, refresh }
}

// ---------------------------------------------------------------------------
// Role resolution helpers
// ---------------------------------------------------------------------------

const SYSTEM_ROLES: string[] = ['GlobalAdmin', 'GlobalViewer', 'ConfigAdmin']
const PORTFOLIO_ROLES: string[] = ['PortfolioManager', 'PortfolioAnalyst', 'ProjectManager', 'ContractsAdmin', 'FinanceManager', 'Viewer']

/**
 * Map the user's role string to a SystemRole.
 * Legacy role names (Admin) map to their new equivalents.
 */
export function resolveSystemRole(role: string): SystemRole {
  if (role === 'Admin') return 'GlobalAdmin'
  if (SYSTEM_ROLES.includes(role)) return role as SystemRole
  return 'None'
}

/**
 * Map the user's role string to a PortfolioRole (if applicable).
 */
export function resolvePortfolioRole(role: string): PortfolioRole | null {
  if (PORTFOLIO_ROLES.includes(role)) return role as PortfolioRole
  return null
}
